// src/native/auth/nativeOAuth.js
//
// WS-2 AUTH-NATIVE. Starts an OAuth sign-in from inside the native shell.
//
// The web path lets Supabase navigate the page to the provider. Inside a native
// webview that is wrong twice over: Apple rejects sign-in flows that navigate
// the app's own webview to a third party (and Google refuses to serve its
// consent screen to an embedded webview at all), and the return trip would have
// to come back through window.location, which a custom scheme deep link never
// touches. So we ask Supabase for the provider URL without navigating
// (skipBrowserRedirect), hand that URL to SFSafariViewController through
// @capacitor/browser, and let deepLinkHandler.js finish the job when the custom
// scheme fires.
//
// Inert on web: isNative() is the first statement, and @capacitor/browser is
// only ever reached through loadNative(), which returns null in a browser, so
// the plugin chunk is never requested.

import posthog from 'posthog-js';
import { isNative, nativePlatform, loadNative } from '@/lib/platform';
import { supabase } from '@/lib/supabase';
import {
  NATIVE_OAUTH_REDIRECT,
  NATIVE_PROVIDER_QUERY_PARAMS,
  isNativeOAuthProvider,
} from './nativeAuthConfig';
import { nativeAuthError } from './nativeAuthError';
import { NATIVE_AUTH_EVENT } from './deepLinkHandler';

/**
 * Begin an OAuth sign-in natively.
 *
 * @param {'google'|'apple'|'facebook'} provider
 * @param {object} [options] extra options merged into supabase signInWithOAuth options
 * @returns {Promise<{ handled: boolean, error: Error|null }>}
 *
 * `handled: false` means "this is not a native context, carry on with the web
 * path unchanged". Every caller is written so that a false `handled` falls
 * through to the byte-identical code that runs today.
 */
export async function startNativeOAuth(provider, options = {}) {
  if (!isNative()) return { handled: false, error: null };
  if (!isNativeOAuthProvider(provider)) return { handled: false, error: null };

  const { queryParams: extraQueryParams, ...restOptions } = options || {};

  try {
    posthog.capture('native_oauth_started', { provider, platform: nativePlatform() });
  } catch {}

  try {
    const queryParams = {
      ...(NATIVE_PROVIDER_QUERY_PARAMS[provider] || {}),
      ...(extraQueryParams || {}),
    };

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: NATIVE_OAUTH_REDIRECT,
        // Do not navigate the app's own webview. We want the URL, not the trip.
        skipBrowserRedirect: true,
        ...restOptions,
        queryParams,
      },
    });

    if (error) {
      try {
        posthog.capture('native_oauth_failed', { provider, reason: 'supabase', message: error.message });
      } catch {}
      return { handled: true, error };
    }

    if (!data?.url) {
      try {
        posthog.capture('native_oauth_failed', { provider, reason: 'no_provider_url' });
      } catch {}
      return {
        handled: true,
        error: nativeAuthError('Supabase returned no provider URL', 'native.auth.oauthFailed'),
      };
    }

    const mod = await loadNative(() => import('@capacitor/browser'));
    const Browser = mod?.Browser;
    if (!Browser) {
      try {
        posthog.capture('native_oauth_failed', { provider, reason: 'browser_plugin_missing' });
      } catch {}
      return {
        handled: true,
        error: nativeAuthError('@capacitor/browser is not available', 'native.auth.browserUnavailable'),
      };
    }

    // presentationStyle 'popover' gives the sheet presentation users expect for
    // a sign-in step, and keeps the app visible behind it so the return feels
    // like coming back rather than launching.
    await Browser.open({ url: data.url, presentationStyle: 'popover' });

    try {
      posthog.capture('native_oauth_browser_opened', { provider });
    } catch {}

    return { handled: true, error: null };
  } catch (err) {
    try {
      posthog.capture('native_oauth_failed', { provider, reason: 'exception', message: String(err) });
    } catch {}
    return {
      handled: true,
      error: nativeAuthError('Native OAuth failed to start', 'native.auth.oauthFailed', err),
    };
  }
}

/**
 * Resolve once the deep link handler reaches a terminal outcome.
 *
 * Most callers do not need this. Login.jsx does not: AuthContext sets the user
 * on onAuthStateChange and the existing redirect guard routes the user onward,
 * which is the same thing that happens on the web today.
 *
 * The FB funnel does need it. On the web that funnel encodes its destination in
 * redirectTo (`${origin}${afterFunnelPath}`), so the browser lands the user
 * exactly where the funnel wanted. A custom scheme deep link carries no such
 * destination, so on native the funnel has to navigate itself once the session
 * exists. This is the one place the build spec understates the work: a bare
 * early return there would sign the user in and then strand them on the funnel
 * screen.
 *
 * @returns {Promise<{ status: 'success'|'cancelled'|'error'|'timeout'|'not-native', error?: Error|null }>}
 */
export function awaitNativeAuthResult({ timeoutMs = 180000 } = {}) {
  if (!isNative()) return Promise.resolve({ status: 'not-native' });

  return new Promise((resolve) => {
    let settled = false;
    let timer = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      try {
        window.removeEventListener(NATIVE_AUTH_EVENT, onEvent);
      } catch {}
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    function onEvent(event) {
      const detail = event?.detail || {};
      finish({ status: detail.status || 'error', error: detail.error || null });
    }

    try {
      window.addEventListener(NATIVE_AUTH_EVENT, onEvent);
    } catch {
      finish({ status: 'error', error: nativeAuthError('Cannot listen for auth result', 'native.auth.sessionFailed') });
      return;
    }
    // The user could sit on the provider consent screen for a while, or walk
    // away entirely. Resolving as 'timeout' beats leaking a listener forever.
    timer = setTimeout(() => finish({ status: 'timeout' }), timeoutMs);
  });
}

/**
 * Start OAuth and wait for the deep link to land. The listener is registered
 * before the browser opens, so a very fast return cannot be missed.
 *
 * @returns {Promise<{ handled: boolean, error: Error|null, status?: string }>}
 */
export async function startNativeOAuthAndWait(provider, options = {}) {
  if (!isNative()) return { handled: false, error: null };

  const { timeoutMs, ...oauthOptions } = options || {};
  const pending = awaitNativeAuthResult(timeoutMs ? { timeoutMs } : undefined);

  const started = await startNativeOAuth(provider, oauthOptions);
  if (!started.handled) return started;
  if (started.error) {
    // Settle the pending listener now rather than leaving it on its timeout.
    try {
      window.dispatchEvent(
        new CustomEvent(NATIVE_AUTH_EVENT, { detail: { status: 'error', error: started.error } })
      );
    } catch {}
    return { ...started, status: 'error' };
  }

  const result = await pending;
  return {
    handled: true,
    error: result.status === 'success' ? null : result.error || null,
    status: result.status,
  };
}
